import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clearSession, getToken, makeClient, setSession, tokenValid } from './api';

export interface Me {
  tenant_id: string;
  tenant_name?: string;
  is_platform_admin?: boolean;
  user_id?: string;
  role: string;
  email?: string;
  name?: string;
  phone?: string;
  job_title?: string;
  avatar_url?: string;
  scopes: string[];
  locale: string;
  /** Identidade assinada do widget bFocus (ausente se o servidor não tiver a chave). */
  bfocus?: { user_external_id: string; customer_external_id: string; user_hash: string };
}

interface SessionCtx {
  token: string | null;
  login: (token: string) => void;
  logout: () => void;
}

const Ctx = createContext<SessionCtx>({ token: null, login: () => {}, logout: () => {} });

/** Sessão guardada, se ainda valer. Token vencido é descartado aqui, antes de virar tela. */
function sessaoInicial(): string | null {
  const t = getToken();
  if (t && !tokenValid(t)) {
    clearSession();
    return null;
  }
  return t;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(sessaoInicial);
  const login = useCallback((t: string) => { setSession(t); setToken(t); }, []);
  const logout = useCallback(() => { clearSession(); setToken(null); }, []);
  return <Ctx.Provider value={{ token, login, logout }}>{children}</Ctx.Provider>;
}

export function useSession() {
  return useContext(Ctx);
}

export function useMe(enabled: boolean) {
  return useQuery({
    queryKey: ['me'],
    enabled,
    queryFn: async (): Promise<Me | null> => {
      const { data } = await makeClient().GET('/me');
      return (data as Me) ?? null;
    },
  });
}
